import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Artikel, ArtikelVorgaben, Konto, Kostenstelle, Steuersatz, Unternehmen }
  from "@kundeninfo/api-client";
import { Arbeitsdialog, darfAbbrechen, useBeforeUnload, useBestaetigung, useFormularZustand } from "@kundeninfo/ui";
import { Auswahl, Chip, Eingabe, Formular, Hinweis, Schalter } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../meHuellen.js";
import { MarkdownHinweis } from "../MarkdownHinweis.js";
import { firstParagraph, formatDatum, formatDezimal } from "../../format.js";
import { renderMarkdown } from "../../markdown.js";
import { CYCLE_TEXT, TERM_UNIT_TEXT } from "../../views/artikel/articleTexts.js";
import { ARTICLE_DIALOG_TEXT as T, CHANGED_FIELD_TEXT } from "./articleDialogTexts.js";
import { kontoNachSteuersatz } from "./kontoVorbelegung.js";
import "./ArtikelDialog.css";

/**
 * Der ARTIKEL-DIALOG (Spec Artikelmodul 0.4, „Artikel-Dialog“; Entscheide 766–772, 785–788, 802, 803): anlegen und
 * bearbeiten im Bereich „Artikel“, rechts angeschlagen in der Hülle `Arbeitsdialog` mit der Breitenstufe für
 * zweispaltige Formulare (`zweispaltig`, P13) — das Blatt ist so breit wie das Formular, am Aufrufort steht kein Maß.
 *
 * **Formular (121–134).** EIN `Formular zweispaltig` wie die me-design-Story „Struktur/Formular › Zweispaltig“: Paare
 * aus `<label htmlFor>` und Feld, ohne eigene `beschriftung` und ohne `slot` am Kind — die Paare setzt das Formular
 * selbst. Einzige Ausnahme ist die Einheit im Feldrahmen (`Auswahl slot="einheit"`) an Preis und Laufzeit. Feldlängen
 * nur über `kurz`/`schmal`/`breit`, Pflicht über `pflicht` am Feld und `data-pflicht` am Label. Drei Stufen schaltet
 * das Formular nach seiner Breite selbst (Beschriftung links, oben, einspaltig).
 *
 * **Vorgaben (788).** Geladen werden die des Unternehmens des Artikels (beim Anlegen das Arbeitsunternehmen):
 * Nummernvorschlag, Konto je Steuersatz, Kategorien, Einheiten; dazu die aktiven Steuersätze, die Erlöskonten nach dem
 * Rahmen des Unternehmens und seine Kostenstellen. Beim Anlegen belegt der Dialog nach P17 vor.
 *
 * **Wege.** Speichern prüft erst in der Maske (Feldfehler am Feld, Fokus auf den ersten — kein Alert), fragt dann
 * gestapelt (`useBestaetigung`) und schreibt; ohne Änderung schließt er mit der Quittung „Keine Änderungen.“.
 * Abbrechen, Kreuz und Escape fragen nur bei Änderungen (`darfAbbrechen`). Eine 409 zeigt die Hinweiszeile des
 * Formulars mit „Neu laden“ (791); eine vergebene Nummer lädt die Vorgaben neu und nennt den neuen Vorschlag (P6).
 */
export type ArtikelDialogClient = Pick<ApiClient, "getProductDefaults" | "createProduct" | "updateProduct"
  | "setProductActive" | "getProductCatalog" | "listActiveTaxRates" | "getBookingAccounts" | "getCostCentres">;

export type ArtikelDialogProps = {
  client: ArtikelDialogClient;
  /** Beim Anlegen das Arbeitsunternehmen (772), sonst das Unternehmen des Artikels (788). */
  unternehmen: Unternehmen;
  /** `null` = neuer Artikel. */
  artikel: Artikel | null;
  /** Die gewählte Kategorie des Bereichs — Vorbelegung beim Anlegen (P17). */
  vorbelegteKategorieId: string | null;
  /** Ein Artikel eines zugeschalteten Unternehmens: Der Kopf trägt dessen Chip (788). */
  fremdesUnternehmen: boolean;
  onGespeichert: (a: Artikel) => void;
  /** Geschlossen ohne Schreiben; `quittung` sagt dem Bereich, was er melden soll („Keine Änderungen.“). */
  onSchliessen: (quittung?: string) => void;
};

type Quelle = "" | "MONITOR" | "AIRCALL";
type Takt = "" | keyof typeof CYCLE_TEXT;
type Laufzeiteinheit = keyof typeof TERM_UNIT_TEXT;

/** Der Entwurf der Maske — Auswahlen als Text, „“ heißt „ohne“. */
type Entwurf = {
  productNumber: string; name: string; productCategoryId: string; active: boolean; description: string;
  netUnitPrice: string; unitOfMeasureId: string; taxRateId: string; bookingAccountId: string; costCentreId: string;
  billingDataSource: Quelle; defaultBillingCycle: Takt; defaultTermValue: string; defaultTermUnit: Laufzeiteinheit;
};

type Stammdaten = { vorgaben: ArtikelVorgaben; saetze: Steuersatz[]; konten: Konto[]; stellen: Kostenstelle[] };

/** Die Feldnamen des Rumpfs — 422-Befunde zu ihnen landen am Feld (`bekannteFelder`). */
const FELDER = ["productNumber", "name", "productCategoryId", "description", "netUnitPrice", "unitOfMeasureId",
  "taxRateId", "bookingAccountId", "costCentreId", "billingDataSource", "defaultBillingCycle", "defaultTermValue",
  "defaultTermUnit"] as const;
type Feld = (typeof FELDER)[number];

const BESCHRIFTUNGEN: Readonly<Record<Feld, string>> = {
  productNumber: T.productNumber, name: T.name, productCategoryId: T.category, description: T.description,
  netUnitPrice: T.price, unitOfMeasureId: T.priceUnitLabel, taxRateId: T.taxRate, bookingAccountId: T.account(null),
  costCentreId: T.costCentre, billingDataSource: T.billingSource, defaultBillingCycle: T.billingCycle,
  defaultTermValue: T.term, defaultTermUnit: T.termUnitLabel,
};

const QUELLEN: readonly { wert: Quelle; text: string }[] = [
  { wert: "", text: T.manual }, { wert: "MONITOR", text: T.sourceMonitor }, { wert: "AIRCALL", text: T.sourceAircall },
];
const TAKTE = Object.keys(CYCLE_TEXT) as (keyof typeof CYCLE_TEXT)[];
const LAUFZEITEINHEITEN = Object.keys(TERM_UNIT_TEXT) as Laufzeiteinheit[];
const SATZ = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

const UNGUELTIG = Symbol("ungültig");

/** Das Attribut `breit` an einem Kind ohne eigene React-Prop dafür (die Zelle der Beschreibung mit Vorschau): Das
 *  Formular liest es am direkten Kind und lässt das Paar beide Spalten spannen (134). */
const BREIT: Readonly<Record<string, string>> = { breit: "" };

/** Der Preis aus der Eingabe (`art="dezimal"`): deutsch mit Komma und Tausenderpunkt, ≥ 0, höchstens zwei
 *  Nachkommastellen. Leer → `null`. */
export function preisAusText(text: string): number | null | typeof UNGUELTIG {
  const roh = text.replace(/\s/g, "");
  if (roh === "") return null;
  const normal = roh.includes(",") ? roh.replace(/\./g, "").replace(",", ".")
    : /^\d{1,3}(\.\d{3})+$/.test(roh) ? roh.replace(/\./g, "") : roh;
  return /^\d+(\.\d{1,2})?$/.test(normal) ? Number(normal) : UNGUELTIG;
}

/** Die Laufzeit: leer → `null`, sonst eine ganze Zahl von 1 bis 600 (781). */
function laufzeitAusText(text: string): number | null | typeof UNGUELTIG {
  const roh = text.trim();
  if (roh === "") return null;
  const n = Number(roh);
  return /^\d+$/.test(roh) && n >= 1 && n <= 600 ? n : UNGUELTIG;
}

/** Die erste aktive Einheit nach `sortIndex` und der Satz mit dem höchsten Prozentsatz (P17). */
const ersteEinheit = (v: ArtikelVorgaben): string =>
  [...v.units].sort((a, b) => a.sortIndex - b.sortIndex)[0]?.id ?? "";
const hoechsterSatz = (saetze: readonly Steuersatz[]): Steuersatz | undefined =>
  [...saetze].sort((a, b) => b.ratePercent - a.ratePercent)[0];

function entwurfAus(a: Artikel | null, s: Stammdaten, kategorieId: string | null): Entwurf {
  if (a === null) {
    const satz = hoechsterSatz(s.saetze)?.id ?? "";
    return {
      productNumber: s.vorgaben.nextProductNumber ?? "", name: "",
      productCategoryId: kategorieId !== null && s.vorgaben.categories.some((k) => k.id === kategorieId) ? kategorieId : "",
      active: true, description: "", netUnitPrice: "", unitOfMeasureId: ersteEinheit(s.vorgaben), taxRateId: satz,
      bookingAccountId: kontoNachSteuersatz(null, null, satz, s.vorgaben.accountByTaxRate) ?? "", costCentreId: "",
      billingDataSource: "", defaultBillingCycle: "", defaultTermValue: "", defaultTermUnit: "MONTHS",
    };
  }
  return {
    productNumber: a.productNumber, name: a.name, productCategoryId: a.productCategoryId ?? "", active: a.active,
    description: a.description ?? "", netUnitPrice: Number.isFinite(a.netUnitPrice) ? formatDezimal(a.netUnitPrice) : "",
    unitOfMeasureId: a.unitOfMeasureId, taxRateId: a.taxRateId, bookingAccountId: a.bookingAccountId ?? "",
    costCentreId: a.costCentreId ?? "", billingDataSource: a.billingDataSource ?? "",
    defaultBillingCycle: a.defaultBillingCycle ?? "",
    defaultTermValue: a.defaultTermValue === null ? "" : String(a.defaultTermValue),
    defaultTermUnit: a.defaultTermUnit ?? "MONTHS",
  };
}

/** Die geänderten Felder in Formularfolge — für „Geändert: …“ und die Frage, ob überhaupt etwas zu speichern ist. */
function geaenderteFelder(alt: Entwurf, neu: Entwurf): (keyof typeof CHANGED_FIELD_TEXT)[] {
  const preis = (e: Entwurf): unknown => { const p = preisAusText(e.netUnitPrice); return p === UNGUELTIG ? e.netUnitPrice : p; };
  // Ohne Laufzeit zählt die Einheit nicht (781: ohne Wert wird keine Einheit gespeichert).
  const laufzeit = (e: Entwurf): string => (e.defaultTermValue.trim() === "" ? "" : `${e.defaultTermValue.trim()} ${e.defaultTermUnit}`);
  const gleich: Record<keyof typeof CHANGED_FIELD_TEXT, boolean> = {
    productNumber: alt.productNumber === neu.productNumber, name: alt.name === neu.name,
    productCategoryId: alt.productCategoryId === neu.productCategoryId, active: alt.active === neu.active,
    description: alt.description === neu.description, netUnitPrice: preis(alt) === preis(neu),
    unitOfMeasureId: alt.unitOfMeasureId === neu.unitOfMeasureId, taxRateId: alt.taxRateId === neu.taxRateId,
    bookingAccountId: alt.bookingAccountId === neu.bookingAccountId, costCentreId: alt.costCentreId === neu.costCentreId,
    billingDataSource: alt.billingDataSource === neu.billingDataSource,
    defaultBillingCycle: alt.defaultBillingCycle === neu.defaultBillingCycle, defaultTerm: laufzeit(alt) === laufzeit(neu),
  };
  return (Object.keys(CHANGED_FIELD_TEXT) as (keyof typeof CHANGED_FIELD_TEXT)[]).filter((f) => !gleich[f]);
}

/** Die Prüfung der Maske (die Fachprüfung macht der Server): fehlt etwas Pflichtiges, ist eine Zahl unlesbar? */
function maskenFehler(e: Entwurf): { field: Feld; message: string }[] {
  const preis = preisAusText(e.netUnitPrice);
  return [
    ...(e.productNumber.trim() === "" ? [{ field: "productNumber" as const, message: T.numberMissing }] : []),
    ...(e.name.trim() === "" ? [{ field: "name" as const, message: T.nameMissing }] : []),
    ...(preis === null ? [{ field: "netUnitPrice" as const, message: T.priceMissing }]
      : preis === UNGUELTIG ? [{ field: "netUnitPrice" as const, message: T.priceInvalid }] : []),
    ...(e.unitOfMeasureId === "" ? [{ field: "unitOfMeasureId" as const, message: T.unitMissing }] : []),
    ...(e.taxRateId === "" ? [{ field: "taxRateId" as const, message: T.taxRateMissing }] : []),
    ...(laufzeitAusText(e.defaultTermValue) === UNGUELTIG ? [{ field: "defaultTermValue" as const, message: T.termInvalid }] : []),
  ];
}

/** Der Feldsatz für den Server; wirft die eigene 422, wenn die Maske ihn nicht lesen kann. */
function feldsatz(e: Entwurf) {
  const fehler = maskenFehler(e);
  if (fehler.length > 0) throw new ApiError(422, "EINGABE_UNLESBAR", fehler[0]!.message, fehler);
  const laufzeit = laufzeitAusText(e.defaultTermValue) as number | null;
  return {
    productNumber: e.productNumber.trim(), name: e.name.trim(),
    description: e.description.trim() === "" ? null : e.description,
    unitOfMeasureId: e.unitOfMeasureId, netUnitPrice: preisAusText(e.netUnitPrice) as number, taxRateId: e.taxRateId,
    productCategoryId: e.productCategoryId === "" ? null : e.productCategoryId,
    defaultTermValue: laufzeit, defaultTermUnit: laufzeit === null ? null : e.defaultTermUnit,
    defaultBillingCycle: e.defaultBillingCycle === "" ? null : e.defaultBillingCycle,
    billingDataSource: e.billingDataSource === "" ? null : e.billingDataSource,
    bookingAccountId: e.bookingAccountId === "" ? null : e.bookingAccountId,
    costCentreId: e.costCentreId === "" ? null : e.costCentreId,
  };
}

/** Ein gespeicherter Wert, der nicht in der Auswahl steht (stillgelegt, abgelöst): als eigene Option, damit er beim
 *  Speichern nicht still auf etwas anderes springt. */
const bisherig = (wert: string, bekannt: boolean, text: string = T.previousEntry): ReactElement | null =>
  (wert === "" || bekannt ? null : <option value={wert}>{text}</option>);

export function ArtikelDialog(p: ArtikelDialogProps): ReactElement {
  const { client, unternehmen, artikel } = p;
  const [stamm, setStamm] = useState<Stammdaten | null>(null);
  const [ladefehler, setLadefehler] = useState(false);
  /** Stand beim Öffnen bzw. nach „Neu laden“ — gegen ihn zählt „geändert“; `basis` ist die Version dazu. */
  const [start, setStart] = useState<{ entwurf: Entwurf; basis: Artikel | null } | null>(null);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  /** Nach 422 `NUMMER_VERGEBEN` der neue Vorschlag als Hilfe (P6). */
  const [nummernHilfe, setNummernHilfe] = useState<string | null>(null);
  const { bestaetigen } = useBestaetigung();

  useEffect(() => {
    let gilt = true;
    Promise.all([client.getProductDefaults(unternehmen.id), client.listActiveTaxRates(),
      client.getBookingAccounts({ companyId: unternehmen.id, kind: "REVENUE" }),
      client.getCostCentres({ companyId: unternehmen.id })])
      .then(([vorgaben, saetze, konten, stellen]) => {
        if (!gilt) return;
        const s = { vorgaben, saetze: saetze.items, konten: konten.items, stellen: stellen.items };
        const e = entwurfAus(artikel, s, p.vorbelegteKategorieId);
        setStamm(s); setStart({ entwurf: e, basis: artikel }); setEntwurf(e);
      }, () => { if (gilt) setLadefehler(true); });
    return () => { gilt = false; };
    // Einmal je Dialog: Er wird für jeden Artikel neu eingehängt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const geaendert = start !== null && entwurf !== null ? geaenderteFelder(start.entwurf, entwurf) : [];
  const neu = artikel === null;
  useBeforeUnload(geaendert.length > 0);

  const formular = useFormularZustand<Artikel | null>({
    speichern: async () => {
      if (entwurf === null || start === null) return null;
      const felder = feldsatz(entwurf);
      const ja = await bestaetigen(neu
        ? { titel: T.confirmCreate, text: T.createConsequence(felder.productNumber, felder.name, unternehmen.shortCode),
            bestaetigenText: T.yes, abbrechenText: T.no }
        : { titel: T.confirmSave, text: T.changed(geaendert.map((f) => CHANGED_FIELD_TEXT[f])),
            bestaetigenText: T.yes, abbrechenText: T.no });
      if (!ja) return null;
      try {
        if (start.basis === null) return await client.createProduct({ companyId: unternehmen.id, ...felder });
        let stand = start.basis;
        if (geaendert.some((f) => f !== "active")) {
          stand = await client.updateProduct(stand.id, { ...felder, version: stand.version });
          // Die Felder sind geschrieben: Scheitert gleich das Stilllegen, bleibt für den nächsten Versuch nur es übrig —
          // mit der neuen Version als Basis, sonst liefe er in eine 409 gegen den eigenen Stand.
          setStart({ entwurf: { ...entwurf, active: stand.active }, basis: stand });
        }
        if (entwurf.active !== stand.active) stand = await client.setProductActive(stand.id, stand.version, entwurf.active);
        return stand;
      } catch (fehler) {
        // Wettlauf um die Nummer (784, P6): Vorgaben neu, der neue Vorschlag als Hilfe am Feld.
        if (fehler instanceof ApiError && fehler.status === 422 && fehler.code === "NUMMER_VERGEBEN") {
          const vorgaben = await client.getProductDefaults(unternehmen.id).catch(() => null);
          const naechste = vorgaben?.nextProductNumber ?? null;
          setNummernHilfe(naechste === null ? T.numberNoRange : T.numberNewSuggestion(naechste));
        }
        throw fehler;
      }
    },
    // „Neu laden“ nach 409 (791): der Stand aus dem Katalog des Unternehmens, der Dialog bleibt offen.
    neuLaden: async () => {
      if (artikel === null || stamm === null) return;
      const katalog = await client.getProductCatalog({ companyIds: [artikel.companyId], includeInactive: true });
      const aktuell = katalog.items.find((a) => a.id === artikel.id);
      if (aktuell === undefined) throw new Error(T.reloadGone);
      const e = entwurfAus(aktuell, stamm, null);
      setStart({ entwurf: e, basis: aktuell }); setEntwurf(e);
    },
    bekannteFelder: FELDER,
    beschriftungen: BESCHRIFTUNGEN,
  });
  const laeuft = formular.laeuft;

  async function speichern(): Promise<void> {
    if (!neu && geaendert.length === 0) { p.onSchliessen(T.noChanges); return; }
    const a = await formular.aktion();
    if (a !== undefined && a !== null) p.onGespeichert(a);
  }
  async function abbrechen(): Promise<void> {
    if (laeuft) return;
    if (await darfAbbrechen(geaendert.length > 0)) p.onSchliessen();
  }
  /** „Neu laden“ verwirft die eigenen Eingaben — erst nach der Rückfrage (791). */
  async function neuLaden(): Promise<void> {
    if (await darfAbbrechen(geaendert.length > 0)) await formular.neuLaden();
  }

  const kategorieName = artikel === null ? null
    : stamm?.vorgaben.categories.find((k) => k.id === artikel.productCategoryId)?.name ?? artikel.category ?? T.withoutCategory;
  const pfad = (
    <span className="artd__pfad">
      {artikel === null ? T.pathNew(unternehmen.shortCode) : T.pathEdit(kategorieName ?? T.withoutCategory)}
      {p.fremdesUnternehmen && <Chip farbe={unternehmen.colorHex}>{unternehmen.shortCode}</Chip>}
    </span>
  );
  const meldung = ladefehler ? T.loadError : formular.meldung;

  return (
    <Arbeitsdialog titel={neu ? T.titleNew : T.titleEdit(artikel.productNumber)} panel="artikel" zweispaltig
      pfad={pfad} onAbbrechen={abbrechen} abbrechenGesperrt={laeuft}
      {...(meldung === null ? {} : { meldungen: <Hinweis art="fehler">{meldung}</Hinweis> })}
      fuss={(
        <>
          <KiKnopf deaktiviert={laeuft} onClick={() => { void abbrechen(); }}>{T.cancel}</KiKnopf>
          <KiKnopf art="primaer" deaktiviert={formular.zustand !== "bereit" || entwurf === null}
            onClick={() => { void speichern(); }}>{T.save}</KiKnopf>
        </>
      )}>
      <p className="artd__erklaerung">{T.explanation}</p>
      {stamm === null || entwurf === null
        ? !ladefehler && <p className="artd__laedt" role="status">{T.loading}</p>
        : <ArtikelFormular stamm={stamm} entwurf={entwurf} artikel={artikel} unternehmen={unternehmen}
            fehler={formular.feldFehler} nummernHilfe={nummernHilfe} onNummerGetippt={() => setNummernHilfe(null)}
            formularProps={{ ...formular.formularProps,
              onNeuLaden: () => { void neuLaden(); } }}
            setEntwurf={(f) => setEntwurf((e) => (e === null ? e : f(e)))} />}
    </Arbeitsdialog>
  );
}

type FormularProps = ReturnType<typeof useFormularZustand>["formularProps"];

/** Das zweispaltige Formular selbst — die Feldtabelle der Spec in Lesereihenfolge. */
function ArtikelFormular(p: {
  stamm: Stammdaten; entwurf: Entwurf; artikel: Artikel | null; unternehmen: Unternehmen;
  fehler: Readonly<Record<string, string>>; nummernHilfe: string | null; formularProps: FormularProps;
  /** Tippen in die Artikelnummer nimmt den Vorschlag nach `NUMMER_VERGEBEN` als Hilfe wieder weg. */
  onNummerGetippt: () => void;
  setEntwurf: (f: (e: Entwurf) => Entwurf) => void;
}): ReactElement {
  const { stamm, entwurf: e, fehler } = p;
  const setze = <K extends keyof Entwurf>(feld: K) => (wert: Entwurf[K]): void => p.setEntwurf((alt) => ({ ...alt, [feld]: wert }));
  const fehlerVon = (...felder: Feld[]) => {
    const text = felder.map((f) => fehler[f]).find((t) => t !== undefined);
    return text === undefined ? {} : { fehler: text };
  };
  // Konto folgt dem Steuersatz (768): leer oder zur alten Vorbelegung passend → Vorbelegung des neuen Satzes.
  const waehleSatz = (satz: string): void => p.setEntwurf((alt) => ({ ...alt, taxRateId: satz,
    bookingAccountId: kontoNachSteuersatz(alt.bookingAccountId === "" ? null : alt.bookingAccountId,
      alt.taxRateId === "" ? null : alt.taxRateId, satz, stamm.vorgaben.accountByTaxRate) ?? "" }));

  const einheiten = [...stamm.vorgaben.units].sort((a, b) => a.sortIndex - b.sortIndex);
  const einheitName = einheiten.find((u) => u.id === e.unitOfMeasureId)?.name ?? p.artikel?.unit ?? "";
  const satz = stamm.saetze.find((s) => s.id === e.taxRateId);
  const preis = preisAusText(e.netUnitPrice);
  const brutto = satz === undefined || preis === null || preis === UNGUELTIG ? null
    : `${formatDezimal(Math.round(preis * (100 + satz.ratePercent)) / 100)} ${T.pricePerUnit(einheitName)}`;
  const doppelt = (s: Steuersatz): boolean => stamm.saetze.some((x) => x.id !== s.id && x.ratePercent === s.ratePercent);
  const nummernHilfe = p.nummernHilfe ?? (p.artikel !== null ? T.numberUnique
    : stamm.vorgaben.nextProductNumber === null ? T.numberNoRange : T.numberSuggested);
  const kurztext = firstParagraph(e.description);
  const rahmen = stamm.konten[0]?.chartOfAccounts ?? null;

  return (
    <Formular zweispaltig {...p.formularProps} hinweis={T.conflict} className="artd__formular">
      <h3>{T.sectionMaster}</h3>
      <label htmlFor="artd-unternehmen">{T.company}</label>
      <Eingabe id="artd-unternehmen" nurlesen breit wert={p.unternehmen.name} />
      <label htmlFor="artd-nummer" data-pflicht="">{T.productNumber}</label>
      <Eingabe id="artd-nummer" name="productNumber" schmal pflicht wert={e.productNumber} hilfe={nummernHilfe}
        {...fehlerVon("productNumber")} onGeaendert={(d) => { p.onNummerGetippt(); setze("productNumber")(d.wert); }} />
      <label htmlFor="artd-name" data-pflicht="">{T.name}</label>
      <Eingabe id="artd-name" name="name" pflicht wert={e.name} {...fehlerVon("name")}
        onGeaendert={(d) => setze("name")(d.wert)} />
      <label htmlFor="artd-kategorie">{T.category}</label>
      <Auswahl id="artd-kategorie" name="productCategoryId" wert={e.productCategoryId} {...fehlerVon("productCategoryId")}
        onGeaendert={(d) => setze("productCategoryId")(d.wert)}>
        {stamm.vorgaben.categories.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        {bisherig(e.productCategoryId, stamm.vorgaben.categories.some((k) => k.id === e.productCategoryId),
          p.artikel?.category ?? T.previousEntry)}
        <option value="">{T.withoutCategory}</option>
      </Auswahl>
      {p.artikel !== null && <label htmlFor="artd-still">{T.inactive}</label>}
      {p.artikel !== null && (
        <Schalter id="artd-still" an={!e.active} beschriftung={T.inactiveEffect}
          onGeaendert={(d) => setze("active")(!d.an)} />
      )}
      <label htmlFor="artd-beschreibung">{T.description}</label>
      <div className="artd__beschreibung" {...BREIT}>
        <Eingabe id="artd-beschreibung" name="description" mehrzeilig wachsend zeilen={4} breit wert={e.description}
          hilfe={T.descriptionHelp} {...fehlerVon("description")} onGeaendert={(d) => setze("description")(d.wert)} />
        <div className="artd__vorschau" aria-live="polite">
          <span className="artd__vorschau-titel">{T.previewTitle}</span>
          <div className="artd__kurz">{kurztext === null ? null : renderMarkdown(kurztext)}</div>
        </div>
        <MarkdownHinweis />
      </div>

      <h3>{T.sectionPrice}</h3>
      <label htmlFor="artd-preis" data-pflicht="">{T.price}</label>
      <Eingabe id="artd-preis" name="netUnitPrice" art="dezimal" schmal pflicht wert={e.netUnitPrice}
        {...fehlerVon("netUnitPrice", "unitOfMeasureId")} onGeaendert={(d) => setze("netUnitPrice")(d.wert)}
        onEinheitGeaendert={(d) => setze("unitOfMeasureId")(d.einheit ?? "")}>
        <Auswahl slot="einheit" aria-label={T.priceUnitLabel} wert={e.unitOfMeasureId}>
          {einheiten.map((u) => <option key={u.id} value={u.id}>{T.pricePerUnit(u.name)}</option>)}
          {bisherig(e.unitOfMeasureId, einheiten.some((u) => u.id === e.unitOfMeasureId), T.pricePerUnit(einheitName))}
        </Auswahl>
      </Eingabe>
      <label htmlFor="artd-steuer" data-pflicht="">{T.taxRate}</label>
      <Auswahl id="artd-steuer" name="taxRateId" kurz pflicht wert={e.taxRateId} {...fehlerVon("taxRateId")}
        {...(satz === undefined ? {} : { hilfe: T.taxHelp(satz.name, brutto) })}
        onGeaendert={(d) => waehleSatz(d.wert)}>
        {stamm.saetze.map((s) => (
          <option key={s.id} value={s.id}>
            {doppelt(s) ? T.rateFrom(SATZ.format(s.ratePercent), formatDatum(s.validFrom)) : T.ratePercent(SATZ.format(s.ratePercent))}
          </option>
        ))}
        {bisherig(e.taxRateId, satz !== undefined)}
      </Auswahl>
      <label htmlFor="artd-konto">{T.account(rahmen)}</label>
      <Auswahl id="artd-konto" name="bookingAccountId" wert={e.bookingAccountId} {...fehlerVon("bookingAccountId")}
        {...(e.bookingAccountId === "" && e.active ? { warnung: T.missingAccountWarning } : {})}
        onGeaendert={(d) => setze("bookingAccountId")(d.wert)}>
        <option value="">{T.noAccount}</option>
        {stamm.konten.map((k) => <option key={k.id} value={k.id}>{`${k.accountNumber} ${k.name}`}</option>)}
        {bisherig(e.bookingAccountId, stamm.konten.some((k) => k.id === e.bookingAccountId))}
      </Auswahl>
      <label htmlFor="artd-kostenstelle">{T.costCentre}</label>
      <Auswahl id="artd-kostenstelle" name="costCentreId" wert={e.costCentreId} {...fehlerVon("costCentreId")}
        onGeaendert={(d) => setze("costCentreId")(d.wert)}>
        <option value="">{T.noCostCentre}</option>
        {stamm.stellen.map((k) => <option key={k.id} value={k.id}>{`${k.code} ${k.name}`}</option>)}
        {bisherig(e.costCentreId, stamm.stellen.some((k) => k.id === e.costCentreId))}
      </Auswahl>

      <h3>{T.sectionTerms}</h3>
      <label htmlFor="artd-quelle">{T.billingSource}</label>
      <Auswahl id="artd-quelle" name="billingDataSource" schmal wert={e.billingDataSource}
        {...fehlerVon("billingDataSource")} onGeaendert={(d) => setze("billingDataSource")(d.wert as Quelle)}>
        {QUELLEN.map((q) => <option key={q.wert} value={q.wert}>{q.text}</option>)}
      </Auswahl>
      <label htmlFor="artd-takt">{T.billingCycle}</label>
      <Auswahl id="artd-takt" name="defaultBillingCycle" schmal wert={e.defaultBillingCycle}
        {...fehlerVon("defaultBillingCycle")} onGeaendert={(d) => setze("defaultBillingCycle")(d.wert as Takt)}>
        <option value="">{T.noCycle}</option>
        {TAKTE.map((t) => <option key={t} value={t}>{CYCLE_TEXT[t]}</option>)}
      </Auswahl>
      <label htmlFor="artd-laufzeit">{T.term}</label>
      <Eingabe id="artd-laufzeit" name="defaultTermValue" art="zahl" kurz wert={e.defaultTermValue} hilfe={T.termHelp}
        {...fehlerVon("defaultTermValue", "defaultTermUnit")} onGeaendert={(d) => setze("defaultTermValue")(d.wert)}
        onEinheitGeaendert={(d) => setze("defaultTermUnit")(d.einheit as Laufzeiteinheit)}>
        <Auswahl slot="einheit" aria-label={T.termUnitLabel} wert={e.defaultTermUnit}>
          {LAUFZEITEINHEITEN.map((u) => <option key={u} value={u}>{TERM_UNIT_TEXT[u].mehrere}</option>)}
        </Auswahl>
      </Eingabe>
    </Formular>
  );
}
